import { useNavigate } from "react-router";
import { CheckCircle2 } from "lucide-react";
import { paths } from "@shared-core/routing/routes";
import {
  joinNames,
  linkedCustomerAccessSummary,
  portalUpsellOffer,
} from "@shared-core/domain/customerPortalAccess";
import { usePortalSignInAdapter } from "@shared-core/data/PortalSignInAdapterContext";
import type { PortalSignInOutcome } from "@shared-core/data/customer-portal-access-types";
import { Card } from "../../../app/components/primitives/card";
import { Button } from "../../../app/components/primitives/button";
import { PortalSignInShell } from "./PortalSignInShell";
import { PortalReviewBar } from "./PortalReviewBar";
import { PortalSignInOutcomeGate } from "./PortalSignInOutcomeGate";

// First sign-in. Nothing here needs doing: the person was already linked to
// their customer(s) before they signed in (Pollards added them, or they were a
// contact on a quote or order). So this page is welcome and orientation, not a
// setup step, and it says plainly what they can and cannot do yet so that a
// short orders list does not read as broken. It does not say how they were
// matched: the server does not tell the portal, and the page need not say which.
// decision: docs/features/customer-storefront/12_decision_access_for_every_customer.md,
// 23_p4_overview.md Decision 8

type Welcome = Extract<PortalSignInOutcome, { kind: "welcome" }>;

function firstName(name: string | null): string | null {
  return name?.split(" ")[0] ?? null;
}

function WelcomeBody({
  outcome,
  scenario,
}: {
  outcome: Welcome;
  scenario: string | undefined;
}) {
  const adapter = usePortalSignInAdapter();
  const navigate = useNavigate();
  // A blocked customer is not somewhere they can go yet; the chooser names it if there are several.
  const customers = outcome.customers.filter((c) => !c.is_blocked);
  const several = customers.length > 1;
  const only = customers[0];
  const ownWorkOnly = customers.filter((c) => !c.has_customer_wide_view);
  // Offered per customer, only where the person lacks it: never what they already hold.
  const upsell = portalUpsellOffer(customers);
  const name = firstName(outcome.person_name);

  function carryOn() {
    // The first data call after this records the visit, so the next sign-in goes straight in.
    adapter.acknowledgeWelcome();
    if (several || !only) {
      // `change` so a customer remembered on this device does not skip the choice.
      navigate(paths.portalChooseCustomer({ scenario, change: true }));
      return;
    }
    adapter.chooseCustomer(only.customer_id, false);
    // Home: the branded home where the theme read succeeds, Orders otherwise (PortalHomePage decides).
    navigate(paths.portalHome());
  }

  return (
    <PortalSignInShell
      title={name ? `Welcome, ${name}` : "Welcome"}
      subtitle="You're all set up."
      footer={<PortalReviewBar current={scenario} />}
    >
      <Card className="gap-0 divide-y p-0">
        <p className="px-4 py-3 text-sm font-medium">
          {several
            ? `You're linked to ${customers.length} customers`
            : "You're linked to"}
        </p>
        {customers.map((c) => (
          <div key={c.customer_id} className="flex items-start gap-3 px-4 py-3">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-green-700 dark:text-green-400" />
            <div className="text-sm">
              <p className="font-medium">{c.customer_name}</p>
              <p className="text-muted-foreground">
                {linkedCustomerAccessSummary(c)}
              </p>
            </div>
          </div>
        ))}
      </Card>

      {(ownWorkOnly.length > 0 || upsell) && (
        <Card className="gap-2 p-4 text-sm">
          {ownWorkOnly.length > 0 && (
            <>
              <p className="font-medium">
                {ownWorkOnly.length < customers.length
                  ? `What you can do at ${joinNames(ownWorkOnly.map((c) => c.customer_name))}`
                  : "What you can do"}
              </p>
              <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                <li>
                  See the orders you place, and the quotes and proofs we send
                  you.
                </li>
                <li>
                  Approve proofs, upload artwork and check it before it prints.
                </li>
                <li>Order from the catalogue and pay by card.</li>
              </ul>
            </>
          )}
          {upsell && <p className="text-muted-foreground">{upsell}</p>}
        </Card>
      )}

      <Button className="w-full" onClick={carryOn}>
        {several
          ? "Choose a customer"
          : `Go to ${only?.customer_name ?? "your"} orders`}
      </Button>
    </PortalSignInShell>
  );
}

export function PortalWelcomePage() {
  return (
    <PortalSignInOutcomeGate kinds={["welcome"] as const}>
      {(outcome, scenario) => (
        <WelcomeBody outcome={outcome} scenario={scenario} />
      )}
    </PortalSignInOutcomeGate>
  );
}
